
import { Component, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { ProfileInterface, ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import CreateFeedbackForm from '@/services/Dossier/feedback/forms/CreateFeedbackForm';
import DossierFeedbackRepositoryInterface, {
  AgentFeedbackInterface,
  FeedbackCategoryInterface, FeedbackChatCommentInterface,
  FeedbackTypeInterface, UpdateAgentFeedbackInterface,
  feedbackEditorToolbar, FeedbackTicketCommentInterface,
} from '@/services/Dossier/feedback/interfaces';
import DossierFeedbackReviewRepositoryInterface, {
  ReviewCategoryInterface,
} from '@/services/Dossier/review/interfaces';
import {
  ProfileMapInterface,
  ReviewCommentInterface,
  WSTicketCommentInterface,
} from '@/services/QC-V2/chat/interfaces';
import AddFeedbackChatCommentsDialog from '@/components/dialogs/dossier/feedback/AddFeedbackChatCommentsDialog.vue';
import { VueEditor } from 'vue2-editor';
import AddFeedbackTicketCommentsDialog from '@/components/dialogs/dossier/feedback/AddFeedbackTicketCommentsDialog.vue';

@Component({
  components: {
    AddFeedbackTicketCommentsDialog,
    VueEditor,
    AddFeedbackChatCommentsDialog,
  },
  mixins: [
    Themeable,
  ],
  inject: [
    'dossierFeedbackRepository',
    'dossierFeedbackReviewRepository',
    'profileRepository',
  ],
  sockets: {
    feedbackChatComments(msg: { comments: ReviewCommentInterface }): void {
      this.$emit('feedback-chat-comments', msg.comments);
    },
    feedbackTicketComments(msg: { comments: ReviewCommentInterface }): void {
      this.$emit('feedback-ticket-comments', msg.comments);
    },
  },
})
export default class UpdateAgentFeedback extends Vue {
  private loading: boolean = true;

  private userId!: string;
  private feedbackId!: number;
  private profile!: ProfileInterface;
  private feedback!: AgentFeedbackInterface;
  private feedbackCategories: FeedbackCategoryInterface[] = [];
  private feedbackTypes: FeedbackTypeInterface[] = [];
  private reviewCategories: ReviewCategoryInterface[] = [];
  private reviewCategoriesForSelects: ReviewCategoryInterface[][] = [];

  private dossierFeedbackRepository!: DossierFeedbackRepositoryInterface;
  private dossierFeedbackReviewRepository!: DossierFeedbackReviewRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;

  private feedbackChatComments: FeedbackChatCommentInterface[] = [];
  private feedbackTicketComments: FeedbackTicketCommentInterface[] = [];
  private profilesMap: ProfileMapInterface = {};

  private form: WPXFormValue<CreateFeedbackForm> = new WPXFormValue(CreateFeedbackForm);
  private forMonthMenu: boolean = false;
  private initialFormUpdate: boolean = true;

  private selectedChatComments: FeedbackChatCommentInterface[] = [];
  private selectedTicketComments: FeedbackTicketCommentInterface[] = [];
  private addFeedbackChatCommentDialogVisible: boolean = false;
  private addFeedbackTicketCommentDialogVisible: boolean = false;

  private toolbar: unknown[] = feedbackEditorToolbar;

  private months: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  private beforeCreate(): void {
    this.$on(
      'feedback-chat-comments',
      (comments: ReviewCommentInterface[]) => this.handleChatCommentsMessage(comments),
    );
    this.$on(
      'feedback-ticket-comments',
      (comments: WSTicketCommentInterface[]) => this.handleTicketCommentsMessage(comments),
    );
  }

  private updated(): void {
    this.initialFormUpdate = false;
  }

  private async created(): Promise<void> {
    if (!this.$route.params.feedbackId) {
      this.$errorDialog.show('Feedback id not provided!');
      return;
    }
    this.feedbackId = Number(this.$route.params.feedbackId);

    if (this.$route.params.userId === this.$store.getters.id) {
      this.$errorDialog.show('You cannot edit your own feedback!');
      return;
    }

    this.userId = this.$route.params.userId;
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      this.feedback = await this.dossierFeedbackRepository.getFeedback(this.feedbackId);
      this.feedbackCategories = await this.dossierFeedbackRepository.getFeedbackCategories();
      this.feedbackTypes = await this.dossierFeedbackRepository.getFeedbackTypes();
      this.reviewCategories = await this.dossierFeedbackReviewRepository.getReviewCategories();
      this.profile = await this.profileRepository.getOneByUserId(this.userId);
      this.assignFormData();
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
    }
  }

  private assignFormData(): void {
    this.form.data = {
      ...this.form.data,
      title: this.feedback.title,
      typeId: this.feedback.type.id,
      categoryId: this.feedback.category.id,
      forMonth: this.feedback.forMonth.substr(0, 7),
      reviews: this.feedback.reviews.map((review) => ({
        categoryId: review.category.id,
        body: review.body,
      })),
    };

    if (!this.form.data.reviews.length) {
      this.addReview();
    }

    this.form.data.reviews.forEach(() => {
      this.reviewCategoriesForSelects.push(this.reviewCategories);
    });

    this.filterReviewCategories();
  }

  private async updateFeedback(): Promise<void> {
    try {
      const data: UpdateAgentFeedbackInterface = {
        categoryId: this.form.data.categoryId,
        typeId: this.form.data.typeId,
        title: this.form.data.title,
        reviews: this.form.data.reviews,
        forMonth: this.form.data.forMonth,
        chatComments: this.selectedChatComments.map((c) => ({
          chatId: c.chatId,
          reviewId: c.reviewId,
          commentId: c.commentId,
        })),
        ticketComments: this.selectedTicketComments.map((c) => ({
          ticketNumber: c.ticketNumber,
          reviewId: c.reviewId,
          commentId: c.commentId,
        })),
      };

      await this.dossierFeedbackRepository.updateFeedback(this.feedback.id, data);
      this.$successDialog.show('Feedback Created Successfully');
      await this.goBack();
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
    }
  }

  private addReview(): void {
    this.form.data.reviews.push({
      categoryId: 0,
      body: '',
    });

    this.filterReviewCategories();
  }

  private removeReview(index: number): void {
    this.form.data.reviews.splice(index, 1);
    this.filterReviewCategories();
  }

  private filterReviewCategories(): void {
    this.form.data.reviews.forEach((_, index) => {
      this.reviewCategoriesForSelects[index] = this.reviewCategoriesForSelects
        .flat()
        .filter((category, categoryIndex, self) => self.indexOf(category) === categoryIndex)
        .filter((category) => category.id === this.form.data.reviews[index].categoryId
          || !this.form.data.reviews.map((formReview) => formReview.categoryId).includes(category.id))
        .sort((categoryA, categoryB) => categoryA.id - categoryB.id);
    });
  }

  private async handleChatCommentsMessage(comments: ReviewCommentInterface[]): Promise<void> {
    if (this.feedbackChatComments.length) {
      return;
    }

    if (!this.feedback) {
      setTimeout(() => {
        this.handleChatCommentsMessage(comments);
      }, 200);
      return;
    }

    const ids: string[] = [];
    const chatComments: FeedbackChatCommentInterface[] = [];
    comments.forEach((comment) => {
      const index = this.feedback.chatComments.findIndex((c) => c.commentId === comment.id);
      if (index === -1) {
        return;
      }

      chatComments.push({
        chatId: this.feedback.chatComments[index].chatId,
        reviewId: this.feedback.chatComments[index].reviewId,
        commentId: comment.id,
        author: comment.author,
        text: comment.text,
        timestamp: comment.timestamp,
      });

      if (!ids.includes(comment.author)) {
        ids.push(comment.author);
      }
    });

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });

    this.feedbackChatComments = chatComments;
    this.selectedChatComments.push(...this.feedbackChatComments);
  }

  private async handleTicketCommentsMessage(comments: WSTicketCommentInterface[]): Promise<void> {
    if (this.feedbackTicketComments.length) {
      return;
    }

    if (!this.feedback) {
      setTimeout(() => {
        this.handleTicketCommentsMessage(comments);
      }, 200);
      return;
    }

    const ids: string[] = [];
    const ticketComments: FeedbackTicketCommentInterface[] = [];
    comments.forEach((comment) => {
      const index = this.feedback.ticketComments.findIndex((c) => c.commentId === comment.id);
      if (index === -1) {
        return;
      }

      ticketComments.push({
        ticketNumber: this.feedback.ticketComments[index].ticketNumber,
        reviewId: this.feedback.ticketComments[index].reviewId,
        ticketId: comment.ticketId,
        commentId: comment.id,
        author: comment.author,
        text: comment.text,
        timestamp: comment.timestamp,
      });

      if (!ids.includes(comment.author)) {
        ids.push(comment.author);
      }
    });

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });

    this.feedbackTicketComments = ticketComments;
    this.selectedTicketComments.push(...this.feedbackTicketComments);
  }

  private getChatCommentLabel(comment: FeedbackChatCommentInterface): string {
    return `${comment.chatId}
    - ${this.profilesMap[comment.author].firstName} ${this.profilesMap[comment.author].lastName}: ${comment.text}`;
  }

  private getTicketCommentLabel(comment: FeedbackTicketCommentInterface): string {
    return `#${comment.ticketNumber}
    - ${this.profilesMap[comment.author].firstName} ${this.profilesMap[comment.author].lastName}: ${comment.text}`;
  }

  private showChatCommentsDialog(): void {
    this.addFeedbackChatCommentDialogVisible = true;
  }

  private showTicketCommentsDialog(): void {
    this.addFeedbackTicketCommentDialogVisible = true;
  }

  private addChatCommentsFromDialog(comments: FeedbackChatCommentInterface[], profilesMap: ProfileMapInterface): void {
    this.feedbackChatComments.push(...comments);
    this.selectedChatComments.push(...comments);
    this.profilesMap = {
      ...this.profilesMap,
      ...profilesMap,
    };
    this.$forceUpdate();
  }

  private addTicketCommentsFromDialog(
    comments: FeedbackTicketCommentInterface[],
    profilesMap: ProfileMapInterface,
  ): void {
    this.feedbackTicketComments.push(...comments);
    this.selectedTicketComments.push(...comments);
    this.profilesMap = {
      ...this.profilesMap,
      ...profilesMap,
    };
    this.$forceUpdate();
  }

  private checkFeedbackCategory(): boolean {
    const feedbackCategory = this.feedbackCategories.find((category) => category.id === this.form.data.categoryId);

    return !!feedbackCategory && feedbackCategory.name === 'QC Feedback';
  }

  @Watch('form.data.forMonth')
  private updateMonthRelatedData(): void {
    if (this.initialFormUpdate) {
      return;
    }

    const date = this.form.data.forMonth ? this.form.data.forMonth : new Date();
    const currentDate = new Date(date);

    let typeName = 'Regular';
    if ([2, 8].includes(currentDate.getMonth())) {
      typeName = 'Attestation';
    }

    const feedbackType = this.feedbackTypes.find((type) => type.name === typeName);
    if (!feedbackType) {
      return;
    }
    this.form.data.typeId = feedbackType.id;

    let title = `${this.months[currentDate.getMonth()]} ${currentDate.getFullYear()} Feedback`;
    if (this.form.data.typeId === 2) {
      title = `${title} (Attestation)`;
    }

    this.form.data.title = title;
  }

  private async goBack(): Promise<void> {
    await this.$router.push({ name: 'view-feedback', params: { feedbackId: String(this.feedbackId) } });
  }
}
